import { useState, useEffect } from 'react';
import Layout from '../components/Layout';
import { Settings, Users, Edit, Save, X } from 'lucide-react';
import { Card } from '../components/ui/card';
import { toast } from 'sonner';
import { authFetch, API } from '../utils/api';

function LeaveCredits({ user }) {
  const [globalSettings, setGlobalSettings] = useState(null);
  const [employeeBalances, setEmployeeBalances] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingGlobal, setEditingGlobal] = useState(false);
  const [editingEmployee, setEditingEmployee] = useState(null);
  const [globalForm, setGlobalForm] = useState({
    default_sick_leave: 15,
    default_vacation_leave: 15,
    default_emergency_leave: 5
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [settingsRes, balancesRes] = await Promise.all([
        authFetch(`${API}/leave/settings`),
        authFetch(`${API}/leave/balances/all`)
      ]);

      if (settingsRes.ok) {
        const settings = await settingsRes.json();
        setGlobalSettings(settings);
        setGlobalForm({
          default_sick_leave: settings.default_sick_leave,
          default_vacation_leave: settings.default_vacation_leave,
          default_emergency_leave: settings.default_emergency_leave
        });
      }

      if (balancesRes.ok) {
        const balances = await balancesRes.json();
        setEmployeeBalances(balances);
      }
    } catch (error) {
      toast.error('Failed to fetch leave credits data');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateGlobalSettings = async () => {
    try {
      const response = await authFetch(`${API}/leave/settings`, {
        method: 'PUT',
        body: JSON.stringify(globalForm)
      });

      if (response.ok) {
        toast.success('Global settings updated!');
        setEditingGlobal(false);
        fetchData();
      } else {
        toast.error('Failed to update settings');
      }
    } catch (error) {
      toast.error('Failed to update settings');
    }
  };

  const handleUpdateEmployeeBalance = async (userId) => {
    const employee = employeeBalances.find(e => e.user_id === userId);
    try {
      const response = await authFetch(`${API}/leave/balance/${userId}`, {
        method: 'PUT',
        body: JSON.stringify({
          sick_leave: employee.sick_leave,
          vacation_leave: employee.vacation_leave,
          emergency_leave: employee.emergency_leave
        })
      });

      if (response.ok) {
        toast.success('Employee balance updated!');
        setEditingEmployee(null);
        fetchData();
      } else {
        toast.error('Failed to update balance');
      }
    } catch (error) {
      toast.error('Failed to update balance');
    }
  };

  const updateEmployeeBalance = (userId, field, value) => {
    setEmployeeBalances(balances =>
      balances.map(balance =>
        balance.user_id === userId
          ? { ...balance, [field]: parseFloat(value) || 0 }
          : balance
      )
    );
  };

  if (user?.role !== 'admin') {
    return (
      <Layout user={user}>
        <div className="max-w-4xl mx-auto text-center py-12">
          <p className="text-foreground-muted">You don't have permission to access this page.</p>
        </div>
      </Layout>
    );
  }

  if (loading) {
    return (
      <Layout user={user}>
        <div className="flex justify-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout user={user}>
      <div className="max-w-7xl mx-auto">
        <div className="mb-8">
          <h1 className="text-3xl font-heading font-bold text-foreground-DEFAULT">Leave Credits Management</h1>
          <p className="text-foreground-muted mt-2">Manage global leave settings and individual employee balances</p>
        </div>

        {/* Global Settings */}
        <Card className="p-6 mb-8">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
              <div className="bg-primary/10 rounded-xl p-3">
                <Settings className="w-6 h-6 text-primary" />
              </div>
              <div>
                <h2 className="text-xl font-heading font-bold text-foreground-DEFAULT">Global Leave Settings</h2>
                <p className="text-sm text-foreground-muted">Default leave credits for new employees</p>
              </div>
            </div>
            {!editingGlobal ? (
              <button
                onClick={() => setEditingGlobal(true)}
                className="flex items-center gap-2 px-4 py-2 bg-slate-100 hover:bg-slate-200 rounded-lg transition-all"
              >
                <Edit className="w-4 h-4" />
                Edit
              </button>
            ) : (
              <div className="flex gap-2">
                <button
                  onClick={() => {
                    setEditingGlobal(false);
                    setGlobalForm({
                      default_sick_leave: globalSettings.default_sick_leave,
                      default_vacation_leave: globalSettings.default_vacation_leave,
                      default_emergency_leave: globalSettings.default_emergency_leave
                    });
                  }}
                  className="flex items-center gap-2 px-4 py-2 bg-slate-100 hover:bg-slate-200 rounded-lg transition-all"
                >
                  <X className="w-4 h-4" />
                  Cancel
                </button>
                <button
                  onClick={handleUpdateGlobalSettings}
                  className="flex items-center gap-2 px-4 py-2 bg-primary hover:bg-primary-hover text-white rounded-lg transition-all"
                >
                  <Save className="w-4 h-4" />
                  Save
                </button>
              </div>
            )}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div>
              <label className="block text-sm font-medium text-foreground-DEFAULT mb-2">
                Default Sick Leave (days/year)
              </label>
              {editingGlobal ? (
                <input
                  type="number"
                  step="0.5"
                  min="0"
                  value={globalForm.default_sick_leave}
                  onChange={(e) => setGlobalForm({ ...globalForm, default_sick_leave: parseFloat(e.target.value) })}
                  className="w-full px-4 py-3 bg-slate-50 border-transparent focus:bg-white focus:border-primary/20 focus:ring-2 focus:ring-primary/10 transition-all rounded-lg"
                />
              ) : (
                <div className="px-4 py-3 bg-slate-50 rounded-lg">
                  <span className="text-2xl font-bold text-foreground-DEFAULT">{globalSettings?.default_sick_leave}</span>
                  <span className="text-foreground-muted ml-2">days</span>
                </div>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-foreground-DEFAULT mb-2">
                Default Vacation Leave (days/year)
              </label>
              {editingGlobal ? (
                <input
                  type="number"
                  step="0.5"
                  min="0"
                  value={globalForm.default_vacation_leave}
                  onChange={(e) => setGlobalForm({ ...globalForm, default_vacation_leave: parseFloat(e.target.value) })}
                  className="w-full px-4 py-3 bg-slate-50 border-transparent focus:bg-white focus:border-primary/20 focus:ring-2 focus:ring-primary/10 transition-all rounded-lg"
                />
              ) : (
                <div className="px-4 py-3 bg-slate-50 rounded-lg">
                  <span className="text-2xl font-bold text-foreground-DEFAULT">{globalSettings?.default_vacation_leave}</span>
                  <span className="text-foreground-muted ml-2">days</span>
                </div>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-foreground-DEFAULT mb-2">
                Default Emergency Leave (days/year)
              </label>
              {editingGlobal ? (
                <input
                  type="number"
                  step="0.5"
                  min="0"
                  value={globalForm.default_emergency_leave}
                  onChange={(e) => setGlobalForm({ ...globalForm, default_emergency_leave: parseFloat(e.target.value) })}
                  className="w-full px-4 py-3 bg-slate-50 border-transparent focus:bg-white focus:border-primary/20 focus:ring-2 focus:ring-primary/10 transition-all rounded-lg"
                />
              ) : (
                <div className="px-4 py-3 bg-slate-50 rounded-lg">
                  <span className="text-2xl font-bold text-foreground-DEFAULT">{globalSettings?.default_emergency_leave}</span>
                  <span className="text-foreground-muted ml-2">days</span>
                </div>
              )}
            </div>
          </div>
        </Card>

        {/* Employee Balances */}
        <Card className="p-6">
          <div className="flex items-center gap-3 mb-6">
            <div className="bg-accent/10 rounded-xl p-3">
              <Users className="w-6 h-6 text-accent" />
            </div>
            <div>
              <h2 className="text-xl font-heading font-bold text-foreground-DEFAULT">Employee Leave Balances</h2>
              <p className="text-sm text-foreground-muted">Individual leave credits for {new Date().getFullYear()}</p>
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-slate-200">
                  <th className="text-left py-3 px-4 font-semibold text-foreground-DEFAULT">Employee</th>
                  <th className="text-left py-3 px-4 font-semibold text-foreground-DEFAULT">Role</th>
                  <th className="text-right py-3 px-4 font-semibold text-foreground-DEFAULT">Sick Leave</th>
                  <th className="text-right py-3 px-4 font-semibold text-foreground-DEFAULT">Vacation Leave</th>
                  <th className="text-right py-3 px-4 font-semibold text-foreground-DEFAULT">Emergency Leave</th>
                  <th className="text-right py-3 px-4 font-semibold text-foreground-DEFAULT">Actions</th>
                </tr>
              </thead>
              <tbody>
                {employeeBalances.map((employee) => (
                  <tr key={employee.user_id} className="border-b border-slate-100 hover:bg-slate-50">
                    <td className="py-3 px-4">
                      <div>
                        <p className="font-medium text-foreground-DEFAULT">{employee.name}</p>
                        <p className="text-sm text-foreground-muted">{employee.email}</p>
                      </div>
                    </td>
                    <td className="py-3 px-4">
                      <span className={`text-xs px-2 py-1 rounded-full ${
                        employee.role === 'admin'
                          ? 'bg-purple-100 text-purple-800'
                          : 'bg-blue-100 text-blue-800'
                      }`}>
                        {employee.role}
                      </span>
                    </td>
                    <td className="py-3 px-4 text-right">
                      {editingEmployee === employee.user_id ? (
                        <input
                          type="number"
                          step="0.5"
                          min="0"
                          value={employee.sick_leave}
                          onChange={(e) => updateEmployeeBalance(employee.user_id, 'sick_leave', e.target.value)}
                          className="w-24 px-2 py-1 text-right bg-slate-50 border border-slate-200 rounded"
                        />
                      ) : (
                        <span className={`font-semibold ${
                          employee.sick_leave < 3 ? 'text-red-600' : 'text-foreground-DEFAULT'
                        }`}>
                          {employee.sick_leave} days
                        </span>
                      )}
                    </td>
                    <td className="py-3 px-4 text-right">
                      {editingEmployee === employee.user_id ? (
                        <input
                          type="number"
                          step="0.5"
                          min="0"
                          value={employee.vacation_leave}
                          onChange={(e) => updateEmployeeBalance(employee.user_id, 'vacation_leave', e.target.value)}
                          className="w-24 px-2 py-1 text-right bg-slate-50 border border-slate-200 rounded"
                        />
                      ) : (
                        <span className={`font-semibold ${
                          employee.vacation_leave < 3 ? 'text-red-600' : 'text-foreground-DEFAULT'
                        }`}>
                          {employee.vacation_leave} days
                        </span>
                      )}
                    </td>
                    <td className="py-3 px-4 text-right">
                      {editingEmployee === employee.user_id ? (
                        <input
                          type="number"
                          step="0.5"
                          min="0"
                          value={employee.emergency_leave}
                          onChange={(e) => updateEmployeeBalance(employee.user_id, 'emergency_leave', e.target.value)}
                          className="w-24 px-2 py-1 text-right bg-slate-50 border border-slate-200 rounded"
                        />
                      ) : (
                        <span className={`font-semibold ${
                          employee.emergency_leave < 1 ? 'text-red-600' : 'text-foreground-DEFAULT'
                        }`}>
                          {employee.emergency_leave} days
                        </span>
                      )}
                    </td>
                    <td className="py-3 px-4 text-right">
                      {editingEmployee === employee.user_id ? (
                        <div className="flex gap-2 justify-end">
                          <button
                            onClick={() => {
                              setEditingEmployee(null);
                              fetchData();
                            }}
                            className="p-2 hover:bg-slate-200 rounded-lg transition-colors"
                          >
                            <X className="w-4 h-4 text-slate-600" />
                          </button>
                          <button
                            onClick={() => handleUpdateEmployeeBalance(employee.user_id)}
                            className="p-2 hover:bg-green-100 rounded-lg transition-colors"
                          >
                            <Save className="w-4 h-4 text-green-600" />
                          </button>
                        </div>
                      ) : (
                        <button
                          onClick={() => setEditingEmployee(employee.user_id)}
                          className="p-2 hover:bg-slate-200 rounded-lg transition-colors"
                        >
                          <Edit className="w-4 h-4 text-slate-600" />
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      </div>
    </Layout>
  );
}

export default LeaveCredits;
